import React, { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { BookOpen, Brain, HelpCircle, Heart, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react'
import { useRouteTranslation } from '@/hooks/useRouteTranslation'

interface PreShahadaStep {
  id: number
  icon: React.ReactNode
  title: string
  description: string
  details: string[]
}

const CARD_GAP_PX = 16

export default function PreShahadaJourney() {
  const { t, lng: langBase } = useRouteTranslation()
  const isRTL = langBase === 'ar' || langBase === 'ur'

  const preShahadaSteps: PreShahadaStep[] = [
    {
      id: 1,
      icon: <BookOpen className="w-8 h-8" />,
      title: t('islam.preShahada.step1.title', 'فهم الإسلام'),
      description: t('islam.preShahada.step1.desc', 'التعرف على أساسيات الدين الإسلامي ومبادئه'),
      details: [
        t('islam.preShahada.step1.detail1', 'معرفة أن الله واحد لا شريك له'),
        t('islam.preShahada.step1.detail2', 'فهم رسالة النبي محمد ﷺ'),
        t('islam.preShahada.step1.detail3', 'التعرف على أركان الإسلام الخمسة'),
        t('islam.preShahada.step1.detail4', 'معرفة أركان الإيمان الستة'),
      ],
    },
    {
      id: 2,
      icon: <Brain className="w-8 h-8" />,
      title: t('islam.preShahada.step2.title', 'دراسة المعلومات الصحيحة'),
      description: t('islam.preShahada.step2.desc', 'الاعتماد على مصادر موثوقة في التعلم'),
      details: [
        t('islam.preShahada.step2.detail1', 'قراءة القرآن الكريم بترجمة معتمدة'),
        t('islam.preShahada.step2.detail2', 'الاطلاع على سيرة النبي ﷺ'),
        t('islam.preShahada.step2.detail3', 'مشاهدة محاضرات علماء موثوقين'),
        t('islam.preShahada.step2.detail4', 'قراءة كتب تعريفية عن الإسلام'),
      ],
    },
    {
      id: 3,
      icon: <HelpCircle className="w-8 h-8" />,
      title: t('islam.preShahada.step3.title', 'طرح الأسئلة'),
      description: t('islam.preShahada.step3.desc', 'لا تتردد في السؤال عن أي شيء يحيرك'),
      details: [
        t('islam.preShahada.step3.detail1', 'التواصل مع مسلمين ملتزمين'),
        t('islam.preShahada.step3.detail2', 'زيارة المركز الإسلامي المحلي'),
        t('islam.preShahada.step3.detail3', 'طرح الأسئلة على العلماء والدعاة'),
        t('islam.preShahada.step3.detail4', 'المشاركة في حلقات التعريف بالإسلام'),
      ],
    },
    {
      id: 4,
      icon: <Heart className="w-8 h-8" />,
      title: t('islam.preShahada.step4.title', 'التأمل والتفكر'),
      description: t('islam.preShahada.step4.desc', 'خذ وقتك في التفكير والتأمل'),
      details: [
        t('islam.preShahada.step4.detail1', 'التفكر في خلق الله وعظمته'),
        t('islam.preShahada.step4.detail2', 'مراجعة ما تعلمته بهدوء'),
        t('islam.preShahada.step4.detail3', 'التأمل في معنى الحياة والغاية منها'),
        t('islam.preShahada.step4.detail4', 'الاستماع إلى قلبك وعقلك'),
      ],
    },
    {
      id: 5,
      icon: <CheckCircle className="w-8 h-8" />,
      title: t('islam.preShahada.step5.title', 'الاستعداد للإقرار'),
      description: t('islam.preShahada.step5.desc', 'عندما تشعر بالطمأنينة والقناعة التامة'),
      details: [
        t('islam.preShahada.step5.detail1', 'التأكد من فهم معنى الشهادتين'),
        t('islam.preShahada.step5.detail2', 'الإيمان الكامل بما ستنطق به'),
        t('islam.preShahada.step5.detail3', 'الاستعداد النفسي والروحي'),
        t('islam.preShahada.step5.detail4', 'اتخاذ القرار عن قناعة واطمئنان'),
      ],
    },
  ]

  const n = preShahadaSteps.length
  const [focusIndex, setFocusIndex] = useState(0)
  const viewportRef = useRef<HTMLDivElement>(null)
  const [viewportW, setViewportW] = useState(0)

  useLayoutEffect(() => {
    const el = viewportRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setViewportW(el.clientWidth))
    ro.observe(el)
    setViewportW(el.clientWidth)
    return () => ro.disconnect()
  }, [])

  const cardW = viewportW > 0 ? Math.floor((viewportW - CARD_GAP_PX) / 2) : 0

  const translatePx = useMemo(() => {
    if (viewportW <= 0 || cardW <= 0) return 0
    const trackWidth = n * cardW + (n - 1) * CARD_GAP_PX
    const minT = trackWidth > viewportW ? -(trackWidth - viewportW) : 0
    if (focusIndex === 0) return 0
    const raw = -(focusIndex * (cardW + CARD_GAP_PX) + cardW / 2 - viewportW / 2)
    return Math.max(minT, Math.min(0, raw))
  }, [focusIndex, viewportW, cardW, n])

  const nextStep = () => setFocusIndex((i) => Math.min(i + 1, n - 1))
  const prevStep = () => setFocusIndex((i) => Math.max(i - 1, 0))

  const renderCard = (step: PreShahadaStep) => (
    <div
      key={step.id}
      dir={isRTL ? 'rtl' : 'ltr'}
      className="shrink-0 rounded-xl bg-white p-5 text-start dark:bg-gray-700 md:p-6"
      style={{
        width: cardW > 0 ? cardW : undefined,
        minWidth: cardW > 0 ? cardW : undefined,
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(16, 24, 40, 0.06)',
        border: '1px solid rgba(16, 24, 40, 0.06)',
      }}
    >
      <div
        className={`relative mb-3 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}
      >
        <span className="z-10 text-3xl font-bold text-primary-600 dark:text-primary-400" style={{ fontWeight: 700 }}>
          {step.id}
        </span>
        <div className="z-0 flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-primary-300 bg-[#F4FBF7] dark:border-primary-500/50 dark:bg-gray-700">
          <div className="text-primary-600 dark:text-primary-400">
            {React.cloneElement(step.icon as React.ReactElement, {
              className: 'w-6 h-6',
              strokeWidth: 2.5,
            })}
          </div>
        </div>
      </div>
      <h3 className="mb-2 text-base font-bold text-gray-900 dark:text-white" style={{ fontWeight: 600 }}>
        {step.title}
      </h3>
      <p className="mb-3 text-xs" style={{ color: '#6B7280' }}>
        {step.description}
      </p>
      <ul className="space-y-2">
        {step.details.map((detail, idx) => (
          <li key={idx} className="flex items-start gap-2 text-xs" style={{ color: '#6B7280' }}>
            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-primary-600 dark:bg-primary-400" />
            <span>{detail}</span>
          </li>
        ))}
      </ul>
    </div>
  )

  return (
    <section className="bg-white py-8 dark:bg-gray-900 md:py-10">
      <div className="container mx-auto max-w-7xl px-4">
        <div
          dir={isRTL ? 'rtl' : 'ltr'}
          lang={langBase}
          className="rounded-2xl bg-gradient-to-br from-[#EAF7F2] to-[#DFF3EA] p-6 dark:bg-green-900/30 md:p-8 lg:p-10"
          style={{ boxShadow: '0 6px 14px rgba(16, 24, 40, 0.08)', border: '1px solid rgba(16, 24, 40, 0.04)' }}
        >
          {/* DOM order: content then image. With dir=rtl the grid mirrors: content right, image left */}
          <div className="grid min-h-0 items-stretch gap-6 lg:grid-cols-[minmax(0,1fr)_280px] lg:gap-10">
            <div className="flex min-h-0 min-w-0 flex-col">
              <div className="mb-5 md:mb-6">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between lg:gap-8">
                  <div className="min-w-0 flex-1 text-start">
                    <h2 className="mb-3 text-xl font-bold leading-tight text-gray-900 dark:text-white md:text-2xl lg:text-3xl" style={{ fontWeight: 700 }}>
                      <span className="block">
                        {t('islam.preShahada.titleLine1', 'The journey of learning about Islam')}
                      </span>
                      <span className="mt-0.5 block">
                        {t('islam.preShahada.titleLine2Before', 'before the ')}
                        <span className="text-primary-600">{t('islam.preShahada.titleHighlight', 'Shahada')}</span>
                      </span>
                    </h2>
                    <p
                      className="max-w-[600px] text-start text-xs leading-relaxed text-gray-600 dark:text-gray-300 md:text-sm"
                      style={{ lineHeight: '1.55' }}
                    >
                      {t(
                        'islam.preShahada.intro',
                        'Take your time to learn and reflect. Islam encourages you to seek, ask, and be sure. This is an important spiritual journey, and every step deserves contemplation and care.',
                      )}
                    </p>
                  </div>
                  <div
                    className={`flex w-full shrink-0 items-center gap-2 lg:w-auto lg:justify-end lg:pt-1 ${isRTL ? 'justify-start' : 'justify-end'}`}
                  >
                    <button
                      type="button"
                      onClick={prevStep}
                      disabled={focusIndex === 0}
                      className={`flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg transition-all duration-300 md:h-9 md:w-9 md:min-h-0 md:min-w-0 ${
                        focusIndex === 0
                          ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                          : 'border border-gray-300 bg-white text-gray-600 hover:border-primary-500 hover:text-primary-600 active:bg-gray-50'
                      }`}
                      aria-label={t('common.previous', 'Previous')}
                    >
                      <ChevronLeft className={`h-5 w-5 md:h-4 md:w-4 ${isRTL ? 'rotate-180' : ''}`} strokeWidth={2} />
                    </button>
                    <button
                      type="button"
                      onClick={nextStep}
                      disabled={focusIndex >= n - 1}
                      className={`flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg transition-all duration-300 md:h-9 md:w-9 md:min-h-0 md:min-w-0 ${
                        focusIndex >= n - 1
                          ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                          : 'bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-700'
                      }`}
                      aria-label={t('common.next', 'Next')}
                    >
                      <ChevronRight className={`h-5 w-5 md:h-4 md:w-4 ${isRTL ? 'rotate-180' : ''}`} strokeWidth={2} />
                    </button>
                  </div>
                </div>
              </div>

              <div ref={viewportRef} className="mb-5 min-w-0 overflow-hidden md:mb-6" dir="ltr">
                <div
                  className="flex touch-pan-y will-change-transform ease-[cubic-bezier(0.22,1,0.36,1)]"
                  style={{
                    gap: CARD_GAP_PX,
                    transform: `translate3d(${translatePx}px,0,0)`,
                    transition: 'transform 520ms cubic-bezier(0.22, 1, 0.36, 1)',
                  }}
                >
                  {preShahadaSteps.map((step) => renderCard(step))}
                </div>
              </div>

              <div
                className="rounded-[22px] bg-[#E8FBF4] p-8 text-start shadow-[0_8px_28px_rgba(16,24,40,0.06)] dark:bg-gray-800/95 dark:shadow-[0_12px_32px_rgba(0,0,0,0.35)]"
              >
                <p className="mb-3 text-base font-bold tracking-tight text-[#0D4F3C] dark:text-emerald-300 md:text-lg">
                  {t('common.remember', 'Remember')}
                  {' :'}
                </p>
                <p
                  className="text-[15px] leading-[1.7] text-gray-900 md:text-base dark:text-gray-200"
                  style={{ fontWeight: 400 }}
                >
                  <span className="block">
                    {t(
                      'islam.preShahada.footerLine1',
                      'Islam is a religion of fitrah, mind and heart together. It does not ask you to believe in anything that contradicts',
                    )}
                  </span>
                  <span className="block">
                    {t(
                      'islam.preShahada.footerLine2',
                      'your mind or sound nature. Take your time, ask, search, and reflect. And Allah guides whom He wills to a',
                    )}
                  </span>
                  <span className="block">
                    {t('islam.preShahada.footerLine3', 'straight path.')}
                  </span>
                </p>
              </div>
            </div>

            <div className="flex w-full min-h-[240px] flex-col lg:h-full lg:min-h-0" style={{ flexShrink: 0 }}>
              <div className="h-full min-h-[240px] flex-1 overflow-hidden rounded-[20px] shadow-[0_8px_28px_rgba(16,24,40,0.08)] lg:min-h-0">
                <img
                  src="/assets/journytolearn.png"
                  alt={t('islam.preShahada.imageAlt', 'Man reading Quran')}
                  className="block h-full min-h-[240px] w-full object-cover object-center lg:min-h-0"
                  onError={(e) => {
                    const target = e.currentTarget
                    target.style.display = 'none'
                    const parent = target.parentElement
                    if (parent) {
                      parent.innerHTML = `
                        <div class="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-700">
                          <div class="text-center p-8">
                            <svg class="w-24 h-24 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                            </svg>
                            <p class="text-gray-600 dark:text-gray-300 font-medium">Reading Quran</p>
                          </div>
                        </div>
                      `
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
